// src/components/RatingsTable.tsx
import React from "react";
import StatusPill from "./StatusPill";
import MomentumPill from "./MomentumPill";
import { fmtUSD, toNumber } from "../utils/format";

type DealRow = {
  company?: string | null;
  platform?: string | null;
  status?: string | null;
  momentum?: string | null;
  amount_raised?: number | string | null;
  [key: string]: unknown;
};

type RatingsTableProps = {
  rows: DealRow[];
  onSort: (key: string) => void;
  sortKey: string;
  sortDir: "asc" | "desc";
};

function RatingsTable({ rows, onSort, sortKey, sortDir }: RatingsTableProps) {
  const Head = (label: string, sortKeyName?: string, right?: boolean) => (
    <th
      onClick={sortKeyName ? () => onSort(sortKeyName) : undefined}
      className={`px-4 py-2 font-medium ${
        sortKeyName ? "cursor-pointer select-none" : ""
      } ${right ? "text-right" : "text-left"}`}
    >
      <div
        className={`inline-flex items-center gap-1 ${
          right ? "justify-end" : ""
        }`}
      >
        <span>{label}</span>
        {sortKey === sortKeyName && (
          <span className="text-slate-400">
            {sortDir === "asc" ? "▲" : "▼"}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Latest deals
          </div>
          <div className="text-xs text-slate-500">
            Top companies in the current filter
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              {Head("Name")}
              {Head("Platform")}
              {Head("Momentum", "momentum")}
              {Head("Status", "status")}
              {Head("Amount Raised", "amount_raised", true)}
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <tr
                key={`${d.company}-${i}`}
                className="border-t border-slate-100 hover:bg-slate-50/80"
              >
                <td className="px-4 py-3 font-medium text-slate-900">
                  {d.company}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {d.platform || "—"}
                </td>
                <td className="px-4 py-3">
                  <MomentumPill momentum={d.momentum ?? undefined} />
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={d.status ?? undefined} />
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {fmtUSD(toNumber(d.amount_raised))}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  No rows for this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { RatingsTable };
